/* eslint-disable no-custom-hooks-in-presentation-components/no-custom-hooks-in-presentation-components */
/* eslint-disable no-state-in-presentation-components/no-state-in-presentation-components */
import { Box, createStyles, Stack, useMantineTheme } from '@mantine/core';
import type { BiMetricConfig } from '@repo/api-codegen';

import { Text } from '@repo/foundations';
import { transition } from '@repo/theme/primitives';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { cleanUpTooltips } from '../../../utils/charts/TooltipMark/tooltipMark';
import { ChartConfigHelper } from '../../../utils/charts/bi/ChartConfigHelper';
import { renderBarChart } from '../../../utils/charts/bi/renderBarChart';
import { renderLineChart } from '../../../utils/charts/bi/renderLineChart';
import { renderPieChart } from '../../../utils/charts/bi/renderPieChart';
import { renderScatterChart } from '../../../utils/charts/bi/renderScatterChart';
import type { LegendsInfo } from '../../../utils/charts/bi/types';
import { useChartContainer } from '../hooks';
import {
	attachLegendAttributes,
	attachPieChartHoverHandlers,
	updateChartOpacityByLegend,
} from '../utils';
import { BiMetricResultsRender } from './BiMetricResultsRender.tsx';
import { BiMetricSingleNumberChart } from './BiMetricSingleNumberChart.tsx';
import { ChartLegend } from './ChartLegend';

const useStyles = createStyles(() => ({
	chartContainer: {
		width: '100%',
		flex: 1,
		flexGrow: 1,
		'& svg [data-legend]': {
			...transition('duration-200', 'ease')('opacity'),
			transitionProperty: 'fill-opacity, stroke-opacity',
		},
	},
}));

function BIChartComponentInternal({
	results,
	chartConfig,
}: {
	results: Record<string, any>[];
	chartConfig: BiMetricConfig['chart_config'];
}) {
	const { classes } = useStyles();
	const [legends, setLegends] = useState<LegendsInfo>({});
	const { containerRef, chartWidth, chartHeight, theme } = useChartContainer();
	const [hoveredLegendName, setHoveredLegendName] = useState<string | null>(
		null
	);

	const mantineTheme = useMantineTheme();

	const chartConfiguration = useMemo(
		() => new ChartConfigHelper(chartConfig, mantineTheme),
		[chartConfig, mantineTheme]
	);

	// Extract chart rendering logic
	const renderChart = useCallback(() => {
		const commonProps = {
			results,
			chartConfig,
			containerRef,
			chartWidth: chartWidth ?? 0,
			chartHeight: chartHeight ?? 0,
			theme,
			chartConfigHelper: chartConfiguration,
		};

		switch (chartConfig.chartType) {
			case 'line':
				return renderLineChart(commonProps);
			case 'bar':
				return renderBarChart(commonProps);
			case 'pie':
				return renderPieChart(commonProps);
			case 'scatter':
				return renderScatterChart(commonProps);
			default:
				return null;
		}
	}, [
		results,
		chartConfig,
		containerRef,
		chartWidth,
		chartHeight,
		theme,
		chartConfiguration,
	]);

	// Main chart rendering effect
	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		const chartResult = renderChart();
		if (!chartResult) return;

		const { plot, legends: chartLegends } = chartResult;
		setLegends(chartLegends);

		// eslint-disable-next-line consistent-return
		return () => {
			plot?.remove();
			cleanUpTooltips();
		};
	}, [renderChart]);

	// Add data attributes to chart elements, set up hover handlers, and update opacity
	// eslint-disable-next-line no-use-effect/no-use-effect
	useEffect(() => {
		if (!containerRef.current || Object.keys(legends).length === 0) return;

		const svg = containerRef.current.querySelector('svg');
		if (!svg) return;

		attachLegendAttributes(svg, legends);
		updateChartOpacityByLegend(svg, legends, hoveredLegendName);

		const cleanup = attachPieChartHoverHandlers(svg, setHoveredLegendName);

		// eslint-disable-next-line consistent-return
		return cleanup ?? undefined;
	}, [legends, containerRef, hoveredLegendName]);

	const getComponent = () => {
		if (chartConfig.chartType === 'number') return BiMetricSingleNumberChart;
		if (chartConfig.chartType === 'table') return BiMetricResultsRender;
		return null;
	};

	const Component = getComponent();

	return (
		<Stack w="100%" h="100%" spacing={0} p={0}>
			{!Component && (
				<ChartLegend
					chartWidth={chartWidth ?? 100}
					legends={legends}
					hoveredLegendName={hoveredLegendName}
					onLegendHover={setHoveredLegendName}
					chartConfigHelper={chartConfiguration}
				/>
			)}
			<Box ref={containerRef} className={classes.chartContainer}>
				{Component && (
					<Component results={results} chartConfig={chartConfig as any} />
				)}
			</Box>
		</Stack>
	);
}

function validateChartConfig(
	chartConfig: BiMetricConfig['chart_config']
): string | null {
	// TODO: Add actual validation logic
	if (
		chartConfig.chartType === 'line' ||
		chartConfig.chartType === 'bar' ||
		chartConfig.chartType === 'scatter' ||
		chartConfig.chartType === 'pie'
	) {
		// Requires x and y axes
		if (
			!chartConfig.axes ||
			!chartConfig.axes.x ||
			chartConfig.axes.x.length === 0
		) {
			return 'At least one x axis is required';
		}

		if (
			!chartConfig.axes ||
			!chartConfig.axes.y ||
			chartConfig.axes.y.length === 0
		) {
			return 'At least one y axis is required';
		}
	}

	return null;
}

function ChartError({ error }: { error: string }) {
	return (
		<Stack w="100%" h="100%" align="center" justify="center" spacing={0}>
			<Text size="sm" color="text/secondary/default">
				{error}
			</Text>
		</Stack>
	);
}

export function BiChartComponent({
	results,
	chartConfig,
}: {
	results: Record<string, any>[];
	chartConfig: BiMetricConfig['chart_config'];
}) {
	// Validation config
	const validationError = validateChartConfig(chartConfig);

	// Show error if validation fails
	if (validationError) {
		return <ChartError error={validationError} />;
	}

	return (
		<BIChartComponentInternal results={results} chartConfig={chartConfig} />
	);
}
